import { useCallback, useMemo, useState } from 'react';

import type { ImportCandidate, ImportCandidateId } from '../types/rulesImport';

type UseRulesImportStateResult = {
  candidates: ImportCandidate[];
  created: ImportCandidate[];
  updated: ImportCandidate[];
  errors: ImportCandidate[];
  selectedIds: Set<ImportCandidateId>;
  replaceCandidates: (items: ImportCandidate[]) => void;
  mergeCandidates: (items: ImportCandidate[]) => void;
  setSelected: (id: ImportCandidateId, selected: boolean) => void;
  selectMany: (ids: ImportCandidateId[]) => void;
};

export const useRulesImportState = (): UseRulesImportStateResult => {
  const [candidatesById, setCandidatesById] = useState<
    Map<ImportCandidateId, ImportCandidate>
  >(new Map());
  const [selectedIds, setSelectedIds] = useState<Set<ImportCandidateId>>(
    new Set(),
  );

  const candidates = useMemo(
    () => Array.from(candidatesById.values()),
    [candidatesById],
  );

  const created = useMemo(
    () => candidates.filter((c) => c.category === 'created'),
    [candidates],
  );
  const updated = useMemo(
    () => candidates.filter((c) => c.category === 'updated'),
    [candidates],
  );
  const errors = useMemo(
    () => candidates.filter((c) => c.category === 'errors'),
    [candidates],
  );

  const replaceCandidates = useCallback((items: ImportCandidate[]) => {
    setCandidatesById(() => {
      const next = new Map<ImportCandidateId, ImportCandidate>();
      items.forEach((item) => {
        const id = item.id ?? item.rule.uuid;
        next.set(id, { ...item, id });
      });
      return next;
    });
    setSelectedIds(new Set());
  }, []);

  const mergeCandidates = useCallback((items: ImportCandidate[]) => {
    setCandidatesById((prev) => {
      const next = new Map(prev);
      items.forEach((item) => {
        const id = item.id ?? item.rule.uuid;
        next.set(id, { ...item, id });
      });
      return next;
    });
  }, []);

  const setSelected = useCallback(
    (id: ImportCandidateId, selected: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (selected) next.add(id);
        else next.delete(id);
        return next;
      });
    },
    [],
  );

  const selectMany = useCallback((ids: ImportCandidateId[]) => {
    setSelectedIds((prev) => new Set([...prev, ...ids]));
  }, []);

  return {
    candidates,
    created,
    updated,
    errors,
    selectedIds,
    replaceCandidates,
    mergeCandidates,
    setSelected,
    selectMany,
  };
};
